import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IBudget, TCustomerField } from 'modules/checkout/domain/entities';
import {
  hasValidFacialRecognition,
  isCustomerRegistrationIncomplete,
} from 'modules/checkout/presentation/helpers';
// Pelo caminho direto, e nao pelo barril de hooks: useCheckoutBudget importa este
// provider, entao o barril fecharia um ciclo de imports.
import useResetCheckoutState from 'modules/checkout/presentation/hooks/useResetCheckoutState';
import {
  useConfirmedGuardianDocument,
  usePreparedBudgetId,
} from 'modules/checkout/presentation/store';
import { ICustomer } from 'shared/domain/entities';
import {
  TRoutePathsWithParams,
  useNavigation,
} from 'shared/presentation/contexts';

import {
  IPreCheckoutContext,
  PreCheckoutContext,
  TBudgetSource,
  TPreCheckoutStep,
} from '../../hooks/usePreCheckout';
import { useTracking } from '../../hooks/useTracking';

const STEPS_DATA: Record<
  TPreCheckoutStep,
  {
    route: (budgetId: number) => TRoutePathsWithParams;
  }
> = {
  FACIAL_RECOGNITION: {
    route: id => `/checkout/reconhecimento-facial/${id}`,
  },
  BUDGET_SUMMARY: {
    route: id => `/checkout/resumo-orcamento/${id}`,
  },
  FINISH_REGISTRATION: {
    route: id => `/checkout/finalizar-registro/${id}`,
  },
  MULTIPLE_METHODS: {
    route: id => `/checkout/multiplo/${id}`,
  },
  LINK_CHECKOUT: {
    route: id => `/checkout/link-pagamento/${id}`,
  },
};

function getFinishRegistrationStep(params: {
  customer: ICustomer;
  missingFields: TCustomerField[];
  confirmedGuardianDocument: string | null;
}): TPreCheckoutStep | undefined {
  if (
    !isCustomerRegistrationIncomplete(
      params.customer,
      params.missingFields,
      params.confirmedGuardianDocument,
    )
  ) {
    return;
  }

  return 'FINISH_REGISTRATION';
}

function getFacialRecognitionSteps(params: {
  budget: IBudget;
  source: TBudgetSource;
}): TPreCheckoutStep[] {
  // O orçamento do carrinho chega sem forma de pagamento, então aqui ainda não dá para
  // saber se o facial é exigido: quem decide é a forma escolhida, e isso só acontece
  // dentro da tela de checkout. O facial do carrinho roda de lá.
  if (params.source === 'CART') return [];

  if (params.budget.signingMode !== 'FACIAL') return [];

  const isThisBudgetAlreadyValidated = hasValidFacialRecognition({
    customerId: params.budget.customer.id,
    budgetId: params.budget.id,
  });

  if (isThisBudgetAlreadyValidated) return [];

  return ['BUDGET_SUMMARY', 'FACIAL_RECOGNITION'];
}

function getCheckoutStep(params: { budget: IBudget }): TPreCheckoutStep {
  if (params.budget.simultaneousPaymentMethods > 1) {
    return 'MULTIPLE_METHODS';
  }

  return 'LINK_CHECKOUT';
}

const PreCheckoutProvider: React.FC = ({ children }) => {
  const [budgetId, setBudgetId] =
    useState<IPreCheckoutContext['budgetId']>(undefined);
  const [source, setSource] =
    useState<IPreCheckoutContext['source']>(undefined);
  const currentStep = useRef(0);
  const steps = useRef<TPreCheckoutStep[]>([]);
  const { openLinkPayment } = useTracking();
  const navigation = useNavigation();
  const confirmedGuardianDocument = useConfirmedGuardianDocument();
  const [preparedBudgetId, setPreparedBudgetId] = usePreparedBudgetId();
  const resetCheckoutState = useResetCheckoutState();

  const start = useCallback<IPreCheckoutContext['start']>(
    ({ budget, source }) => {
      // Outro orçamento é outra compra: o que ficou do anterior não vale mais. A troca
      // só acontece de fato no carrinho, onde o cliente compra duas vezes sem recarregar
      // a página; comparar o id evita limpar quando é o mesmo checkout sendo reaberto,
      // o que apagaria uma cobrança Pix já gerada.
      if (preparedBudgetId !== budget.id) {
        resetCheckoutState();
        setPreparedBudgetId(budget.id);
      }

      steps.current = [];
      currentStep.current = 0;
      setBudgetId(budget.id);
      setSource(source);
      openLinkPayment({
        budgetId: budget.id.toString(),
      });
      const finishRegistrationStep = getFinishRegistrationStep({
        customer: budget.customer,
        missingFields: budget.missingCustomerFields,
        confirmedGuardianDocument,
      });

      if (finishRegistrationStep) {
        steps.current.push(finishRegistrationStep);
      }

      const facialRecognitionSteps = getFacialRecognitionSteps({
        budget,
        source,
      });
      steps.current.push(...facialRecognitionSteps);

      const checkoutStep = getCheckoutStep({ budget });
      steps.current.push(checkoutStep);

      const firstStep = STEPS_DATA[steps.current[0]];
      const route = firstStep.route(budget.id);
      navigation.push(route);
    },
    [
      navigation,
      confirmedGuardianDocument,
      preparedBudgetId,
      resetCheckoutState,
      setPreparedBudgetId,
    ],
  );

  const nextStep = useCallback<IPreCheckoutContext['nextStep']>(() => {
    const nextStep = currentStep.current + 1;

    const nextStepKey = steps.current[nextStep];

    if (!nextStepKey || !budgetId) return;

    currentStep.current = nextStep;
    const nextStepData = STEPS_DATA[nextStepKey];
    const route = nextStepData.route(budgetId);
    navigation.push(route);
  }, [navigation, budgetId]);

  const hasStep = useCallback<IPreCheckoutContext['hasStep']>(
    step => steps.current.includes(step),
    [],
  );

  // In case the user uses the browser's native navigation handler to go back and forth from the checkout pages,
  // we need to synchronize the currentStep to prevent them from jumping/missing steps
  useEffect(() => {
    if (!budgetId) return;

    const popstateListener = () => {
      const newPathname = document.location.pathname;

      const newPathAssociatedRoute = steps.current.findIndex(
        step => STEPS_DATA[step].route(budgetId) === newPathname,
      );

      currentStep.current = Math.max(newPathAssociatedRoute, 0);
    };

    window.addEventListener('popstate', popstateListener);

    return () => {
      window.removeEventListener('popstate', popstateListener);
    };
  }, [budgetId]);

  return (
    <PreCheckoutContext.Provider
      value={{
        budgetId,
        source,
        activeSteps: steps.current,
        start,
        nextStep,
        hasStep,
      }}
    >
      {children}
    </PreCheckoutContext.Provider>
  );
};

export default PreCheckoutProvider;
